import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Input, toast } from '@metrodev/core-web';
import { getErrorMessage, profileApi } from '../../api';
import { useAuth } from '../../hooks/useAuth';
import { Modal } from '../Modal';
import { useAvatarUrl } from './useAvatarUrl';

const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];
const section: React.CSSProperties = { margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: 'var(--color-text)' };

/**
 * Perfil propio, para cualquier usuario autenticado (interno o de portal): foto a la izquierda, nombre / apellido / email
 * al costado y, debajo, el cambio de contraseña. La foto se sube al instante; los datos y la contraseña se guardan con
 * los botones de abajo.
 */
export function ProfileModal({ onClose }: { onClose: () => void }) {
  const user = useAuth((s) => s.user)!;
  const setUser = useAuth((s) => s.setUser);
  const qc = useQueryClient();
  const avatarUrl = useAvatarUrl();
  const fileInput = useRef<HTMLInputElement>(null);
  const fullName = `${user.firstName} ${user.lastName}`;
  const isComensal = user.role === 'comensal';

  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [email, setEmail] = useState(user.email);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');

  const wantsPasswordChange = !!(currentPassword || newPassword || repeatPassword);
  const passwordError = !wantsPasswordChange
    ? null
    : !currentPassword
      ? 'Ingresá tu contraseña actual'
      : newPassword.length < 8
        ? 'La nueva contraseña debe tener al menos 8 caracteres'
        : newPassword !== repeatPassword
          ? 'Las contraseñas nuevas no coinciden'
          : null;
  const dataValid = firstName.trim() !== '' && lastName.trim() !== '' && /\S+@\S+\.\S+/.test(email);
  const dataChanged = firstName !== user.firstName || lastName !== user.lastName || email !== user.email;

  const refreshUser = (updated: typeof user) => {
    setUser({ ...user, ...updated });
    qc.invalidateQueries({ queryKey: ['profile', 'avatar'] });
  };

  const upload = useMutation({
    mutationFn: (file: File) => profileApi.uploadAvatar(file, file.name),
    onSuccess: (updated) => { refreshUser(updated); toast.success({ title: 'Foto actualizada' }); },
    onError: (err) => toast.error({ title: 'No se pudo subir la foto', description: getErrorMessage(err) }),
  });
  const remove = useMutation({
    mutationFn: () => profileApi.removeAvatar(),
    onSuccess: (updated) => { refreshUser(updated); toast.success({ title: 'Foto quitada' }); },
    onError: (err) => toast.error({ title: 'No se pudo quitar la foto', description: getErrorMessage(err) }),
  });

  const logout = useAuth((s) => s.logout);
  const dataSaved = useRef(false);

  const save = useMutation({
    // Primero los datos y después la contraseña: cambiarla invalida todas las sesiones (también esta), así que ya no se podría guardar nada.
    mutationFn: async () => {
      dataSaved.current = false;
      if (dataChanged) {
        const updated = await profileApi.update({ firstName: firstName.trim(), lastName: lastName.trim(), ...(isComensal ? {} : { email }) });
        refreshUser(updated);
        dataSaved.current = true;
      }
      if (wantsPasswordChange) await profileApi.changePassword({ currentPassword, newPassword });
      return { passwordChanged: wantsPasswordChange };
    },
    onSuccess: ({ passwordChanged }) => {
      if (passwordChanged) {
        // La contraseña nueva cierra todas las sesiones abiertas: se vuelve a iniciar sesión con ella.
        toast.success({ title: 'Contraseña cambiada', description: 'Por seguridad se cerraron tus sesiones. Iniciá sesión con la contraseña nueva.' });
        logout();
        return;
      }
      toast.success({ title: 'Perfil guardado' });
      onClose();
    },
    onError: (err) =>
      toast.error({
        title: dataSaved.current ? 'Se guardaron los datos, pero no la contraseña' : 'No se pudo guardar',
        description: getErrorMessage(err),
      }),
  });

  const onPickFile = (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) return void toast.error({ title: 'Formato no válido', description: 'Usá una imagen PNG, JPG o WEBP.' });
    if (file.size > MAX_AVATAR_BYTES) return void toast.error({ title: 'La imagen pesa demasiado', description: 'El máximo es 2 MB.' });
    upload.mutate(file);
  };

  return (
    <Modal title="Mi perfil" onClose={onClose} maxWidth={640}>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flex: '0 0 150px', margin: '0 auto' }}>
          {avatarUrl ? (
            <img src={avatarUrl} alt={fullName} style={{ width: 130, height: 130, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--color-border)' }} />
          ) : (
            <Avatar name={fullName} size={130} />
          )}
          <input ref={fileInput} type="file" accept={ACCEPTED.join(',')} hidden onChange={(e) => { onPickFile(e.target.files?.[0]); e.target.value = ''; }} />
          <Button type="button" variant="secondary" loading={upload.isPending} onClick={() => fileInput.current?.click()}>
            {user.avatarKey ? 'Cambiar foto' : 'Subir foto'}
          </Button>
          {user.avatarKey && (
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, color: 'var(--color-text-muted)', textDecoration: 'underline' }}
            >
              Quitar foto
            </button>
          )}
          <span style={{ fontSize: 11.5, color: 'var(--color-text-muted)', textAlign: 'center' }}>PNG, JPG o WEBP · hasta 2 MB</span>
        </div>

        <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
          <Input label="Nombre" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input label="Apellido" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          <Input label="Email" type="email" value={email} disabled={isComensal} onChange={(e) => setEmail(e.target.value)} />
        </div>
      </div>

      {!isComensal && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--color-border-divider)' }}>
          <p style={section}>Cambiar contraseña <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(opcional)</span></p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
            <Input label="Contraseña actual" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            <Input label="Nueva contraseña" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            <Input label="Repetir nueva" type="password" autoComplete="new-password" value={repeatPassword} onChange={(e) => setRepeatPassword(e.target.value)} />
          </div>
          {passwordError && <p role="alert" style={{ margin: '8px 0 0', fontSize: 12.5, color: 'var(--color-danger-strong)' }}>{passwordError}</p>}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
        <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button
          type="button"
          variant="primary"
          loading={save.isPending}
          disabled={!dataValid || !!passwordError || (!dataChanged && !wantsPasswordChange)}
          onClick={() => save.mutate()}
        >
          Guardar cambios
        </Button>
      </div>
    </Modal>
  );
}
